/**
 * @package   FlorenceEGI — EGI — CoA PDF Thumbnail Island (M-EGI-351 FASE 3)
 * @author    Padmin D. Curtis (AI Partner OS3.0-Compliant) for Fabio Cherici
 * @version   1.0.0
 * @date      2026-07-01
 * @purpose   Isola Vanilla TS che, su egis-show PUBBLICA, monta l'anteprima PDF collassabile
 *            del CoA attivo. Legge il container [data-coa-pdf-thumb] dentro un <details> e, al
 *            PRIMO apertura del <details> (toggle), chiama renderPdfThumb (lazy → nessun costo
 *            CWV finché l'utente non apre). Il PDF url arriva dal server (data-pdf-url del CoA
 *            attivo, pubblico via route coa.pdf.download) → NESSUNA chiamata al check auth-gated.
 *
 * A11Y:      <details>/<summary> nativo (keyboard + screen reader); container a dimensioni fisse
 *            (no CLS); il canvas riceve role="img"+aria-label dal modulo condiviso.
 * Sicurezza: nessun markup grezzo; l'URL PDF è validato da renderPdfThumb (isSafePdfUrl).
 */

import { renderPdfThumb, type PdfThumbI18n } from "./pdf-thumb";

/** Legge le etichette i18n dai data-attribute del container (server-render, 6 lingue). */
function readI18n(el: HTMLElement): PdfThumbI18n {
  return {
    ariaLabel: el.dataset.i18nAriaLabel ?? "",
    unavailable: el.dataset.i18nUnavailable ?? "",
    error: el.dataset.i18nError ?? "",
  };
}

/** Wire di un singolo blocco anteprima: render UNA sola volta all'apertura del <details>. */
function wire(details: HTMLDetailsElement, thumb: HTMLElement): void {
  const pdfUrl = thumb.dataset.pdfUrl ?? "";
  const checkUrl = thumb.dataset.checkUrl;
  const width = Number(thumb.dataset.thumbWidth ?? "140");
  const i18n = readI18n(thumb);

  let rendered = false;
  const doRender = (): void => {
    if (rendered) return;
    rendered = true;
    void renderPdfThumb(thumb, {
      pdfUrl: pdfUrl !== "" ? pdfUrl : undefined,
      checkUrl: pdfUrl === "" ? checkUrl : undefined,
      width: Number.isFinite(width) && width > 0 ? width : 140,
      i18n,
    });
  };

  details.addEventListener("toggle", () => {
    if (details.open) doRender();
  });

  // Se il <details> è già aperto al load (raro: SSR aperto), renderizza subito.
  if (details.open) doRender();
}

/** Boot: cerca tutti i blocchi anteprima nella pagina e li collega. */
function init(): void {
  document
    .querySelectorAll<HTMLElement>("[data-coa-pdf-thumb]")
    .forEach((thumb) => {
      const details = thumb.closest<HTMLDetailsElement>("details");
      if (details) wire(details, thumb);
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
